import { AlertCircle, ArrowLeft, CalendarDays, CheckCircle2, Clock3, LockKeyhole, MessageSquarePlus, Scale, ShieldCheck, Vote } from "lucide-react";
import { useMutation } from "convex/react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Avatar, Button, Card, EmptyState, Label, PageHeader, Section, Tag } from "../components/ui";
import { convexApi } from "../convexApi";
import { isObserverUser } from "../lib/domain";
import { governanceContext } from "../lib/governanceDemo";
import {
  assertVoteCanBeCast,
  calculateVoteResult,
  calculateMembershipParticipationBonus,
  calculateVotingEntitlement,
  canUserVoteForFirm,
  limitedVotingExplanation,
  maxVotingPeriodDays,
  minVotingPeriodDays,
  nuancedVoteOptions,
  participantAllocationExplanation,
  proposalVoteTimelineStatus,
  splitVoteExplanation,
  type VoteValue,
  validateVotingTimeline,
  voteScore,
  votingClosesAtForDuration,
  votingPeriodDays,
  voteValueLabel,
  voteScoreBandLabel,
} from "../lib/voting";
import type { DiscussionContribution, LexData } from "../lib/types";

const voteOptions = nuancedVoteOptions;

type ProposalPersistence = {
  scheduleVote?: (input: { proposalId: string; votingOpensAt: number; durationDays: number }) => Promise<unknown>;
  castVote?: (input: { proposalId: string; vote: VoteValue; conflictCategory?: string | null }) => Promise<unknown>;
  addContribution?: (input: { discussionId: string; type: "comment" | "note"; body: string }) => Promise<unknown>;
  setPatronage?: (input: { proposalId: string; patron: boolean }) => Promise<unknown>;
};

export function ProposalDetailPage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveProposalDetailPage data={data} />;
  return <ProposalDetailPageContent data={data} />;
}

function LiveProposalDetailPage({ data }: { data: LexData }) {
  const scheduleVote = useMutation(convexApi.governance.scheduleVote);
  const castVote = useMutation(convexApi.voting.castFirmVote);
  const addContribution = useMutation(convexApi.discussions.addContribution);
  const setProposalPatronage = useMutation(convexApi.governance.setProposalPatronage);
  const persistence: ProposalPersistence = {
    scheduleVote: (input) => scheduleVote({ proposalId: input.proposalId as never, votingOpensAt: input.votingOpensAt, durationDays: input.durationDays }),
    castVote: (input) => castVote({ proposalId: input.proposalId as never, vote: input.vote, conflictCategory: input.conflictCategory ?? null }),
    addContribution: (input) => addContribution({ discussionId: input.discussionId as never, type: input.type, body: input.body }),
    setPatronage: (input) => setProposalPatronage({ proposalId: input.proposalId as never, patron: input.patron }),
  };
  return <ProposalDetailPageContent data={data} persistence={persistence} />;
}

function ProposalDetailPageContent({ data, persistence }: { data: LexData; persistence?: ProposalPersistence }) {
  const { proposalId = "prop-1" } = useParams();
  const ctx = useMemo(() => governanceContext(data, proposalId), [data, proposalId]);
  const [selectedVote, setSelectedVote] = useState<VoteValue | null>(null);
  const [conflictCategory, setConflictCategory] = useState("client conflict");
  const [message, setMessage] = useState<string | null>(null);
  const [voting, setVoting] = useState(false);
  const [scheduleByProposal, setScheduleByProposal] = useState<Record<string, { opensAt: number; durationDays: number }>>({});
  const [locallyCalledVotes, setLocallyCalledVotes] = useState<Record<string, boolean>>({});
  const [proposerQuestion, setProposerQuestion] = useState("");
  const [askingProposer, setAskingProposer] = useState(false);
  const [localProposerQuestions, setLocalProposerQuestions] = useState<DiscussionContribution[]>([]);
  const [patronOverride, setPatronOverride] = useState<boolean | null>(null);
  const [savingPatronage, setSavingPatronage] = useState(false);

  if (!ctx) {
    return (
      <>
        <PageHeader title="Proposal" subtitle="The requested proposal could not be found." />
        <EmptyState>Open a proposal from Network projects.</EmptyState>
      </>
    );
  }

  const proposer = data.firms.find((firm) => firm.id === ctx.proposal.proposerFirmId);
  const proposerUser = data.users.find((user) => user.id === ctx.discussion?.createdByUserId) ?? data.users.find((user) => user.firmId === ctx.proposal.proposerFirmId && user.representativeType === "principal") ?? data.users.find((user) => user.firmId === ctx.proposal.proposerFirmId);
  const proposerQuestions = [...data.contributions, ...localProposerQuestions]
    .filter((contribution) => contribution.discussionId === ctx.proposal.discussionId && /^Question to proposer:/i.test(contribution.body))
    .sort((a, b) => b.createdAt - a.createdAt);
  const schedule = scheduleByProposal[ctx.proposal.id] ?? {
    opensAt: ctx.config.votingOpensAt,
    durationDays: Math.min(maxVotingPeriodDays, Math.max(minVotingPeriodDays, votingPeriodDays(ctx.config))),
  };
  const scheduledConfig = {
    ...ctx.config,
    votingOpensAt: schedule.opensAt,
    votingClosesAt: votingClosesAtForDuration(schedule.opensAt, schedule.durationDays),
  };
  const effectiveProposalStatus = locallyCalledVotes[ctx.proposal.id] ? "final_vote" : ctx.proposal.status;
  const timelineStatus = effectiveProposalStatus === "final_vote" || effectiveProposalStatus === "decided" ? proposalVoteTimelineStatus(scheduledConfig) : "scheduled";
  const displayCtx = {
    ...ctx,
    config: scheduledConfig,
    result: calculateVoteResult({ proposal: scheduledConfig, firms: ctx.firms, votes: ctx.votes, rule: ctx.rule, certifiedBy: "Coordination Committee", certifiedAt: Date.now() }),
  };
  const viewerFirm = data.viewerFirm;
  const viewerFirmProfile = displayCtx.firms.find((firm) => firm.id === viewerFirm?.id);
  const viewerCategory = viewerFirm?.membershipCategory ?? "Observer";
  const viewerParticipationBonus = viewerFirm ? calculateMembershipParticipationBonus(ctx.participation, viewerFirm.id, "2026-H1", viewerCategory) : 0;
  const viewerEntitlement = viewerFirm
    ? calculateVotingEntitlement({
        memberEntityId: viewerFirm.id,
        membershipCategory: viewerCategory,
        period: "2026-H1",
        participationBonus: viewerParticipationBonus,
        decisionType: displayCtx.config.decisionType,
        associateLimitedVotingEnabled: displayCtx.rule.associateLimitedVotingEnabled,
        expertLimitedVotingEnabled: displayCtx.rule.expertLimitedVotingEnabled,
      })
    : null;
  const viewerWeight = viewerEntitlement?.totalWeight ?? 0;
  const votingRightsCopy = limitedVotingExplanation({ membershipCategory: viewerCategory, decisionType: displayCtx.config.decisionType, totalWeight: viewerWeight });
  const viewerParticipant = displayCtx.participantAllocations.find((participant) => participant.userId === data.viewer.id) ?? displayCtx.participantAllocations[0];
  const allocatedWeight = viewerParticipant?.allocatedWeight ?? viewerWeight;
  const allocationCopy = participantAllocationExplanation({
    firmTotalWeight: viewerWeight,
    participantCount: displayCtx.participantAllocations.length || 1,
    allocatedWeight,
    splitParticipantVotingAllowed: displayCtx.rule.splitParticipantVotingAllowed,
  });
  const splitCopy = viewerFirm ? splitVoteExplanation(displayCtx.firmAggregates, viewerFirm.id) : null;
  const votingError =
    effectiveProposalStatus !== "final_vote"
      ? "This proposal has not been formally opened for voting."
      : viewerFirm && viewerFirmProfile
      ? assertVoteCanBeCast({
          user: { id: data.viewer.id, firmId: data.viewer.firmId, active: data.viewer.active, canVoteForFirm: data.viewer.representativeType === "principal" || data.viewer.representativeType === "alternate", role: data.viewer.role },
          firm: viewerFirmProfile,
          proposal: displayCtx.config,
          now: Date.now(),
          rule: displayCtx.rule,
        })
      : "Your account is not attached to a voting firm.";
  const representativeCanVote =
    viewerFirmProfile &&
    canUserVoteForFirm(
      { id: data.viewer.id, firmId: data.viewer.firmId, active: data.viewer.active, canVoteForFirm: data.viewer.representativeType === "principal" || data.viewer.representativeType === "alternate", role: data.viewer.role },
      viewerFirmProfile,
    );
  // Any participating member (not observers) can push a proposal to a vote.
  const canScheduleVote = !isObserverUser(data.viewer);  const viewerIsPatron = patronOverride ?? Boolean(data.viewerFirm?.id && ctx.proposal.coSponsorFirmIds.includes(data.viewerFirm.id));
  const originalPatron = Boolean(data.viewerFirm?.id && ctx.proposal.coSponsorFirmIds.includes(data.viewerFirm.id));
  const patronCount = ctx.proposal.coSponsorFirmIds.length + (patronOverride === null || patronOverride === originalPatron ? 0 : patronOverride ? 1 : -1);
  const togglePatronage = async () => {
    if (!persistence?.setPatronage) return;
    setSavingPatronage(true);
    try { await persistence.setPatronage({ proposalId: ctx.proposal.id, patron: !viewerIsPatron }); setPatronOverride(!viewerIsPatron); setMessage(!viewerIsPatron ? "Your firm is now a patron of this proposal." : "Your firm is no longer a patron of this proposal."); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Patronage could not be updated."); }
    finally { setSavingPatronage(false); }
  };

  const callVoteNow = async () => {
    const opensAt = Date.now() - 1000;
    const durationDays = Math.min(maxVotingPeriodDays, Math.max(minVotingPeriodDays, schedule.durationDays));
    try {
      await persistence?.scheduleVote?.({ proposalId: ctx.proposal.id, votingOpensAt: opensAt, durationDays });
      setScheduleByProposal((current) => ({ ...current, [ctx.proposal.id]: { opensAt, durationDays } }));
      setLocallyCalledVotes((current) => ({ ...current, [ctx.proposal.id]: true }));
      setMessage(`Voting is now open and closes ${date(votingClosesAtForDuration(opensAt, durationDays))}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The vote could not be opened.");
    }
  };

  const askProposerForInformation = async () => {
    const body = proposerQuestion.trim();
    if (!body) {
      setMessage("Add the information you need from the proposer first.");
      return;
    }
    const contributionBody = `Question to proposer: ${body}`;
    const now = Date.now();
    setAskingProposer(true);
    try {
      await persistence?.addContribution?.({ discussionId: ctx.proposal.discussionId, type: "comment", body: contributionBody });
      if (!persistence?.addContribution) {
        setLocalProposerQuestions((current) => [{
          id: `local-proposer-question-${now}`,
          discussionId: ctx.proposal.discussionId,
          type: "comment",
          body: contributionBody,
          createdByUserId: data.viewer.id,
          createdByFirmId: data.viewerFirm?.id ?? data.viewer.firmId ?? "",
          createdAt: now,
          updatedAt: now,
        }, ...current]);
      }
      setProposerQuestion("");
      setMessage(`Question sent to ${proposerUser?.name ?? proposer?.name ?? "the proposer"}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The question could not be sent.");
    } finally {
      setAskingProposer(false);
    }
  };

  const recordVote = async () => {
    if (!selectedVote) {
      setMessage("Choose a support, neutral, opposition or recusal position first.");
      return;
    }
    if (votingError) {
      setMessage(votingError);
      return;
    }
    setVoting(true);
    try {
      await persistence?.castVote?.({ proposalId: ctx.proposal.id, vote: selectedVote, conflictCategory: selectedVote === "recuse" ? conflictCategory : null });
      setMessage(`Vote recorded for ${viewerFirm?.name}: ${voteValueLabel(selectedVote)} (${voteScore(selectedVote)})${selectedVote === "recuse" ? ` (${conflictCategory})` : ""}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The vote could not be recorded.");
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to="/network-projects" className="inline-flex min-h-10 items-center gap-2 rounded-lg text-sm font-medium text-secondary hover:text-ink">
        <ArrowLeft className="h-4 w-4" />
        Back to network projects
      </Link>

      <PageHeader
        title={ctx.discussion?.title ?? "Governance proposal"}
        subtitle="Review the proposal text, amendments, nuanced liquid-democracy voting rules and current result snapshot."
        actions={
          <Link to="/my-workspace/delegations" className="inline-flex min-h-11 items-center rounded-lg border border-line bg-surface px-4 text-sm font-semibold text-ink hover:bg-hover">
            Procurations
          </Link>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(340px,0.75fr)]">
        <div className="space-y-5">
          <Card className="p-5">
            <div className="mb-4 flex flex-wrap gap-2">
              <Tag>{displayCtx.config.decisionType} decision</Tag>
              <Tag>{ctx.proposal.status.replace(/_/g, " ")}</Tag>
              <Tag>{displayCtx.rule.majorityType.replace(/_/g, " ")}</Tag>
              <Tag>{timelineStatus === "closed" ? ctx.certifiedSnapshot ? "result certified" : "awaiting certification" : timelineStatus === "open" ? "voting open" : "vote scheduled"}</Tag>
              {displayCtx.config.secretBallot ? <Tag>secret ballot</Tag> : null}
            </div>
              <Label>Proposal text</Label>
            <div className="serif-reading mt-3 whitespace-pre-wrap text-[17px] leading-[1.65] text-ink">{displayCtx.config.finalText}</div>
            <div className="mt-5 grid gap-3 border-t border-divider pt-4 sm:grid-cols-3">
              <RuleFact icon={Scale} label="Quorum" value={`${Math.round(displayCtx.rule.quorumThreshold * 100)}% of eligible firms`} />
              <RuleFact icon={Vote} label="Majority" value={displayCtx.rule.doubleMajorityRequired ? "Headcount and weighted tests" : `${Math.round(displayCtx.rule.majorityThreshold * 100)}% of votes cast`} />
              <RuleFact icon={ShieldCheck} label="Delegation" value={displayCtx.rule.delegationAllowed && displayCtx.config.delegationAllowed ? "Allowed with cap" : "Disabled"} />
            </div>
            <div className="mt-4 rounded-lg border border-divider bg-canvas p-3 text-sm leading-6 text-secondary">
              {displayCtx.config.decisionType === "advisory_poll"
                ? "This is an advisory poll. It records nuanced support and opposition scores but does not decide the proposal."
                : displayCtx.rule.splitParticipantVotingAllowed
                ? "Nuanced liquid-democracy voting is enabled with participant allocation. Each authorised participant casts a scored position from +100 to -100 for only their allocated share, while the firm remains the institutional voting unit."
                : "This decision uses nuanced liquid-democracy voting at firm level. Participant split voting and participation bonus are disabled."}
            </div>
          </Card>

          <ProposerPanel
            proposerName={proposer?.name ?? "Member firm"}
            proposerUserName={proposerUser?.name ?? "Proposing member"}
            proposerUserRole={proposerUser?.role ?? proposerUser?.representativeType ?? "Member"}
            proposerAvatarUrl={proposerUser?.avatarUrl}
            questions={proposerQuestions}
            value={proposerQuestion}
            sending={askingProposer}
            onChange={setProposerQuestion}
            onSend={() => void askProposerForInformation()}
            viewerId={data.viewer.id}
            data={data}
          />

          <Section title="Amendments">
            <div className="grid gap-3">
              {data.amendments.filter((amendment) => amendment.proposalId === ctx.proposal.id).map((amendment) => (
                <Card key={amendment.id} className="p-4">
                  <div className="flex flex-wrap gap-2">
                    <Tag>Amendment {amendment.amendmentNumber}</Tag>
                    <Tag>{amendment.status.replace(/_/g, " ")}</Tag>
                    <Tag>{amendment.type}</Tag>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-secondary">{amendment.rationale}</p>
                  <div className="mt-3 rounded-lg border border-divider bg-canvas p-3 text-sm text-ink">{amendment.proposedText}</div>
                </Card>
              ))}
            </div>
          </Section>

          <ResultsPanel ctx={displayCtx} data={data} timelineStatus={timelineStatus} />
        </div>

        <aside className="space-y-5">
          <VoteTimelineCard
            config={displayCtx.config}
            status={timelineStatus}
            canSchedule={canScheduleVote}
            onSet={async (opensAt, durationDays) => {
              try {
                await persistence?.scheduleVote?.({ proposalId: ctx.proposal.id, votingOpensAt: opensAt, durationDays });
                setScheduleByProposal((current) => ({ ...current, [ctx.proposal.id]: { opensAt, durationDays } }));
                if (opensAt <= Date.now()) setLocallyCalledVotes((current) => ({ ...current, [ctx.proposal.id]: true }));
                setMessage(`Voting timeline set: opens ${date(opensAt)} and closes ${date(votingClosesAtForDuration(opensAt, durationDays))}.`);
              } catch (error) {
                setMessage(error instanceof Error ? error.message : "The voting timeline could not be changed.");
              }
            }}
          />

          {!isObserverUser(data.viewer) ? (
            <Card className="p-5">
              <div className="flex items-start justify-between gap-4"><div><Label>Project patrons</Label><div className="mt-1 font-semibold text-ink">{patronCount} member {patronCount === 1 ? "firm" : "firms"}</div><p className="mt-1 text-sm text-secondary">Patrons publicly stand behind the proposal and appear in the transparency registry.</p></div><Button type="button" variant={viewerIsPatron ? "secondary" : "primary"} disabled={savingPatronage || !persistence?.setPatronage} onClick={() => void togglePatronage()}>{savingPatronage ? "Saving…" : viewerIsPatron ? "Withdraw patronage" : "Patron this proposal"}</Button></div>
            </Card>
          ) : null}
          <Card className="p-5">
            <div className="mb-4 flex items-center gap-3">
              <Avatar name={viewerFirm?.name ?? data.viewer.name} />
              <div>
                <Label>You are voting as</Label>
                <div className="mt-1 font-semibold text-ink">{viewerFirm?.name ?? "No firm attached"}</div>
                <div className="text-sm text-secondary">{data.viewer.name}</div>
              </div>
            </div>

            <div className="grid gap-2 rounded-xl border border-divider bg-canvas p-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-secondary">Decision role</span>
                <span className="font-semibold text-ink">{viewerWeight > 0 ? "Binding vote" : displayCtx.config.decisionType === "advisory_poll" ? "Advisory poll" : "No voting right"}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-secondary">Organisation total weight</span>
                <span className="font-semibold text-ink">{viewerWeight.toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-secondary">Your allocation</span>
                <span className="font-semibold text-ink">{allocatedWeight.toFixed(2)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-secondary">Representative</span>
                <span className="font-semibold text-ink">{representativeCanVote ? "Authorised" : "Not authorised"}</span>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-line bg-canvas p-3 text-sm leading-6 text-secondary">{votingRightsCopy}</div>
            <div className="mt-4 rounded-lg border border-line bg-canvas p-3 text-sm leading-6 text-secondary">{allocationCopy}</div>

            {splitCopy ? <div className="mt-3 rounded-lg border border-line bg-canvas p-3 text-sm leading-6 text-secondary">{splitCopy}</div> : null}

            <div className="mt-4 grid gap-2">
              {voteOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`min-h-12 rounded-lg border px-3 text-left text-sm font-semibold ${selectedVote === option.value ? "border-primary bg-cobaltSoft text-cobaltDeep" : "border-line bg-surface text-ink hover:bg-hover"}`}
                  onClick={() => setSelectedVote(option.value)}
                >
                  <span className="block">{option.label} <span className="text-xs font-medium text-secondary">({option.score > 0 ? "+" : ""}{option.score})</span></span>
                  <span className="mt-0.5 block text-xs font-normal text-secondary">{option.description}</span>
                </button>
              ))}
            </div>

            {selectedVote === "recuse" ? (
              <label className="mt-3 block">
                <span className="mb-1 block text-xs font-medium text-muted">Conflict category</span>
                <select className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" value={conflictCategory} onChange={(event) => setConflictCategory(event.target.value)}>
                  <option>client conflict</option>
                  <option>personal interest</option>
                  <option>competitive interest</option>
                  <option>confidentiality concern</option>
                  <option>other professional obligation</option>
                </select>
              </label>
            ) : null}

            {votingError ? (
              <div className="mt-4 flex gap-2 rounded-lg border border-line bg-canvas p-3 text-sm text-secondary">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <span>{votingError}</span>
              </div>
            ) : null}

            {canScheduleVote && votingError && (votingError === "Voting has not opened." || votingError === "This proposal has not been formally opened for voting.") ? (
              <Button type="button" variant="secondary" className="mt-3 w-full" onClick={() => void callVoteNow()}>
                Open voting now
              </Button>
            ) : null}

            <Button type="button" className="mt-4 w-full" onClick={() => void recordVote()} disabled={voting || Boolean(votingError)}>
              {voting ? "Recording vote..." : votingError ? "Vote unavailable" : "Review and vote"}
            </Button>
            {message ? <div className="mt-3 rounded-lg bg-cobaltSoft px-3 py-2 text-sm text-cobaltDeep">{message}</div> : null}
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-2">
              <LockKeyhole className="h-4 w-4 text-primary" />
              <h2 className="font-semibold text-ink">Decision record</h2>
            </div>
            <p className="mt-2 text-sm leading-6 text-secondary">Results are stored as a snapshot so later changes to membership or participation points do not alter the decision record.</p>
            <div className="mt-4 grid gap-2 text-sm">
              <SnapshotLine label="Proposer" value={proposer?.name ?? "Member firm"} />
              <SnapshotLine label="Voting period" value={`${date(displayCtx.config.votingOpensAt)} - ${date(displayCtx.config.votingClosesAt)}`} />
              <SnapshotLine label="Status" value={timelineStatus === "closed" ? ctx.certifiedSnapshot ? "Closed, result certified" : "Closed, awaiting certification" : timelineStatus === "open" ? "Open for voting" : "Scheduled"} />
              <SnapshotLine label="Published outcome" value={timelineStatus === "closed" && ctx.certifiedSnapshot ? displayCtx.result.finalOutcome.replace(/_/g, " ") : timelineStatus === "closed" ? "Awaiting certification" : "Pending close"} />
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function ProposerPanel({
  proposerName,
  proposerUserName,
  proposerUserRole,
  proposerAvatarUrl,
  questions,
  value,
  sending,
  onChange,
  onSend,
  viewerId,
  data,
}: {
  proposerName: string;
  proposerUserName: string;
  proposerUserRole: string;
  proposerAvatarUrl?: string | null;
  questions: DiscussionContribution[];
  value: string;
  sending: boolean;
  onChange: (value: string) => void;
  onSend: () => void;
  viewerId: string;
  data: LexData;
}) {
  return (
    <Section title="Proposer and questions" kicker="Clarify missing information before voting">
      <Card className="p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 gap-3">
            <Avatar name={proposerUserName} imageUrl={proposerAvatarUrl} />
            <div className="min-w-0">
              <Label>Proposal made by</Label>
              <div className="mt-1 font-semibold text-ink">{proposerUserName}</div>
              <div className="text-sm text-secondary">{proposerName} · {proposerUserRole}</div>
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <label>
              <span className="mb-1 block text-xs font-medium text-muted">Ask the proposer for completion of information</span>
              <textarea
                className="min-h-24 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder="Ask for missing facts, documents, scope, timing or drafting clarification."
              />
            </label>
            <Button type="button" variant="secondary" className="mt-3" onClick={onSend} disabled={sending}>
              <MessageSquarePlus className="mr-2 h-4 w-4" />
              {sending ? "Sending..." : "Ask proposer"}
            </Button>
          </div>
        </div>
        <div className="mt-5 border-t border-divider pt-4">
          <Label>Questions already sent</Label>
          {questions.length > 0 ? (
            <div className="mt-3 grid gap-2">
              {questions.map((question) => {
                const author = data.users.find((user) => user.id === question.createdByUserId);
                const authorUser = question.createdByUserId === viewerId ? data.viewer : author;
                const firm = data.firms.find((item) => item.id === question.createdByFirmId);
                return (
                  <div key={question.id} className="rounded-lg border border-divider bg-canvas px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-secondary">
                      <Avatar name={authorUser?.name ?? "Member"} imageUrl={authorUser?.avatarUrl} size="sm" />
                      <span className="font-medium text-ink">{question.createdByUserId === viewerId ? "You" : author?.name ?? "Member"}</span>
                      <span>{firm?.name}</span>
                      <span>{date(question.createdAt)}</span>
                    </div>
                    <div className="mt-1 text-secondary">{question.body.replace(/^Question to proposer:\s*/i, "")}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-3 rounded-lg border border-divider bg-canvas px-3 py-2 text-sm text-secondary">No questions have been sent to the proposer yet.</div>
          )}
        </div>
      </Card>
    </Section>
  );
}

function VoteTimelineCard({
  config,
  status,
  canSchedule,
  onSet,
}: {
  config: NonNullable<ReturnType<typeof governanceContext>>["config"];
  status: "scheduled" | "open" | "closed";
  canSchedule: boolean;
  onSet: (opensAt: number, durationDays: number) => void | Promise<void>;
}) {
  const [startDate, setStartDate] = useState(dateInputValue(config.votingOpensAt));
  const [durationDays, setDurationDays] = useState(votingPeriodDays(config));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const opensAt = dateInputToTimestamp(startDate);
  const closesAt = votingClosesAtForDuration(opensAt, durationDays);

  const apply = async () => {
    const nextError = validateVotingTimeline({ votingOpensAt: opensAt, votingClosesAt: closesAt });
    setError(nextError);
    if (nextError) return;
    setSaving(true);
    setConfirmation(null);
    try {
      await onSet(opensAt, durationDays);
      setConfirmation(opensAt <= Date.now() ? `Voting is now open and closes ${date(closesAt)}.` : `Vote scheduled for ${date(opensAt)}.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The voting timeline could not be changed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center gap-2">
        <CalendarDays className="h-4 w-4 text-primary" />
        <h2 className="font-semibold text-ink">Voting timeline</h2>
      </div>
      <div className="grid gap-3">
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Opening date</span>
          <input className="min-h-11 w-full rounded-lg border border-line bg-surface px-3" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </label>
        <label>
          <span className="mb-1 block text-xs font-medium text-muted">Voting duration</span>
          <input
            className="min-h-11 w-full rounded-lg border border-line bg-surface px-3"
            type="number"
            min={minVotingPeriodDays}
            max={maxVotingPeriodDays}
            value={durationDays}
            onChange={(event) => setDurationDays(Number(event.target.value) || minVotingPeriodDays)}
          />
        </label>
      </div>
      <div className="mt-3 rounded-lg border border-divider bg-canvas p-3 text-sm leading-6 text-secondary">
        <div className="flex items-center gap-2 font-medium text-ink">
          <Clock3 className="h-4 w-4 text-primary" />
          {status === "closed" ? "Closed, awaiting certification" : status === "open" ? "Open for voting" : "Scheduled"}
        </div>
        <div className="mt-1">Opens {date(opensAt)} · closes {date(closesAt)} · {durationDays} days.</div>
        <div className="mt-1">Voting must stay open for at least 7 days and no more than 21 days. Results publish after certification.</div>
      </div>
      {error ? <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">{error}</div> : null}
      {confirmation ? <div role="status" className="mt-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">{confirmation}</div> : null}
      {canSchedule ? (
        <Button type="button" variant="secondary" className="mt-4 w-full" disabled={saving} onClick={() => void apply()}>
          {saving ? "Opening vote…" : opensAt <= Date.now() ? "Call vote now" : "Schedule vote"}
        </Button>
      ) : (
        <div className="mt-4 rounded-lg border border-line bg-canvas px-3 py-2 text-sm text-secondary">Only the proposer, Coordination Committee or Governance Admin can call or reschedule the vote.</div>
      )}
    </Card>
  );
}

function ResultsPanel({ ctx, data, timelineStatus }: { ctx: NonNullable<ReturnType<typeof governanceContext>>; data: LexData; timelineStatus: "scheduled" | "open" | "closed" }) {
  const capped = ctx.delegations.filter((delegation) => delegation.status === "inactive_for_cap");
  const published = timelineStatus === "closed" && Boolean(ctx.certifiedSnapshot);
  const closedAwaitingCertification = timelineStatus === "closed" && !ctx.certifiedSnapshot;
  return (
    <Section title="Results snapshot">
      <Card className="p-5">
        <div className="mb-4 flex items-center gap-3">
          {published && ctx.result.finalOutcome === "adopted" ? <CheckCircle2 className="h-5 w-5 text-primary" /> : published ? <AlertCircle className="h-5 w-5 text-accent" /> : <Clock3 className="h-5 w-5 text-primary" />}
          <div>
            <Label>{published ? "Certified outcome" : closedAwaitingCertification ? "Certification pending" : "Publication pending"}</Label>
            <div className="mt-1 text-lg font-semibold capitalize text-ink">{published ? ctx.result.finalOutcome.replace(/_/g, " ") : closedAwaitingCertification ? "Awaiting certification" : "Result publishes after certification"}</div>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <ResultMetric label="Quorum" value={`${ctx.result.quorumCount}/${ctx.result.eligibleFirmsCount}`} note={ctx.result.quorumMet ? "met" : "not met"} />
          <ResultMetric label="Participation" value={`${Math.round(ctx.result.participationRate * 100)}%`} note={`${ctx.result.quorumCount}/${ctx.result.eligibleFirmsCount} eligible firms counted`} />
          <ResultMetric label="Support score" value={ctx.result.supportScore.toFixed(0)} note={`${ctx.result.supportWeight.toFixed(2)} support weight / ${ctx.result.supportCount} positions`} />
          <ResultMetric label="Opposition score" value={ctx.result.oppositionScore.toFixed(0)} note={`${ctx.result.oppositionWeight.toFixed(2)} opposition weight / ${ctx.result.oppositionCount} positions`} />
          <ResultMetric label="Score band" value={voteScoreBandLabel(ctx.result.scoreBand)} note={`Net ${ctx.result.netSupportScore.toFixed(0)} · balance ${(ctx.result.scoreBalance * 100).toFixed(0)}%`} />
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {ctx.votes.slice(0, 7).map((vote) => {
            const firm = data.firms.find((item) => item.id === vote.firmId);
            const user = data.users.find((item) => item.id === vote.userId);
            const aggregate = ctx.firmAggregates.find((item) => item.firmId === vote.firmId);
            return (
              <div key={vote.id} className="flex items-center gap-3 rounded-lg border border-divider bg-canvas p-3">
                <Avatar name={user?.name ?? firm?.name ?? vote.firmId} imageUrl={user?.avatarUrl} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-ink">{firm?.name ?? vote.firmId}</div>
                  <div className="text-xs text-secondary">{user?.name ? `${user.name} · ` : ""}Weight {vote.votingWeight.toFixed(2)}</div>
                  {aggregate ? <div className="text-xs text-muted">Firm score: {aggregate.netSupportScore.toFixed(0)} · {voteScoreBandLabel(aggregate.scoreBand)}</div> : null}
                </div>
                <Tag>{voteValueLabel(vote.voteValue)}</Tag>
              </div>
            );
          })}
        </div>
        {capped.length > 0 ? (
          <div className="mt-4 rounded-lg border border-line bg-canvas p-3 text-sm text-secondary">
            {capped.length} procuration{capped.length === 1 ? "" : "s"} cannot be applied because the recipient has reached the cap.
          </div>
        ) : null}
      </Card>
    </Section>
  );
}

function RuleFact({ icon: Icon, label, value }: { icon: typeof Scale; label: string; value: string }) {
  return (
    <div className="rounded-lg border border-divider bg-canvas p-3">
      <Icon className="mb-2 h-4 w-4 text-primary" />
      <Label>{label}</Label>
      <div className="mt-1 text-sm font-semibold text-ink">{value}</div>
    </div>
  );
}

function ResultMetric({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-lg border border-divider bg-canvas p-3">
      <Label>{label}</Label>
      <div className="mt-1 text-xl font-semibold tracking-[-0.02em] text-ink">{value}</div>
      <div className="mt-1 text-xs text-secondary">{note}</div>
    </div>
  );
}

function SnapshotLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-divider pb-2 last:border-b-0">
      <span className="text-secondary">{label}</span>
      <span className="text-right font-medium text-ink">{value}</span>
    </div>
  );
}

function date(value: number) {
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dateInputValue(value: number) {
  return new Date(value).toISOString().slice(0, 10);
}

function dateInputToTimestamp(value: string) {
  const parsed = Date.parse(`${value}T09:00:00`);
  return Number.isFinite(parsed) ? parsed : Date.now();
}
